/* ⚛ REACT */
import { useEffect, useRef, useState } from "react";

/* 📦 LIBS */
import { toast } from "react-toastify";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/* 🧩 COMPONENTS */
import { SelectCitie, CustomLoading } from "../../Components";
import { ModalMap, ModalLine, ListEquipamentos, EquipamentCard } from "./Components/index.js";

/* 🔗 SERVICE */
import { MapRequests } from "../../Services/mapServices/requests.ts";
import { MapService } from "../../Services/mapServices";

/* 🧠 REDUX */
import { store } from "../../Redux/Store";
import { useSelector } from "react-redux/es/hooks/useSelector";
import { ChangeMapShowCaption } from "../../Redux/Actions";

/* 📁 ASSETS*/
import { Advertencia, Critico, Manutencao, PerdaComunicacao, RelogioIcon } from "../../Assets/icons";
import { ETA, Poco, Reservatorio, Pressure, Level, Vazao, GeneralIcon } from "../../Assets/icons";

/* 🎨 STYLES */
import "mapbox-gl/dist/mapbox-gl.css";
import { ContainerMap, SelectContainer, Container, CaptionGroup, LastUpdateText, LastUpdateIcon, CaptionContainer, CaptionContent, CaptionButton, 
    CaptionImg, CaptionItem, CaptionImage, LastUpdateEquipament } from "./styles";

/* 🔧 UTILS */
import { getDataString } from "../../util/Months.js";
import { setJWTToken } from "../../Services/Api.ts";

const AppMap = () => {

    const { t } = useTranslation();

    document.title = t("map.document-title");

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const mapContainerRef = useRef(null);
    const mapServices = useRef(null);
    const mapRequests = useRef(new MapRequests());

    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentCitie, setCurrentCitie] = useState('');
    const [TimeReset, setTimeReset] = useState(getDataString(new Date().getHours()) + ' h ' + getDataString(new Date().getMinutes())+' min');

    const showCaption = useSelector(state => state.mapReducer.showCaption);
    const equipamento = useSelector(state => state.mapReducer.equipamento);
    const dados = useSelector(state => state.mapReducer.dados);
    const isLoadingModal = useSelector(state => state.mapReducer.isLoadingModal);
    const status = useSelector(state => state.mapReducer.state);
    const EquipamentDataList = useSelector(state => state.mapReducer.EquipamentDataList);

    useEffect(() => {
        if (token) {
            localStorage.setItem("token", token);
            setJWTToken(token);
            navigate("/map", { replace: true });
        } else {
            const value = localStorage.getItem("token");
            if (value) {
                setJWTToken(value);
            }
        }
    }, [token, navigate]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            const now = new Date();
            setTimeReset(`${getDataString(now.getHours())} h ${getDataString(now.getMinutes())} min`);
            mapServices.current.restart();
        }, 150000);

        return () => {
            clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        const StartAndGetCities = async() => {
            setLoading(true);
            await mapServices.current.start();
            setLoading(false);
            try{
                let tempCities = await mapRequests.current.RequestCities();
                tempCities.authorized_cities.unshift({
                    name: t("select.city"),
                    value: ''
                })
                setCities(tempCities.authorized_cities)
                // verifica se há cidade salva no localstorage 
                // se não verifica se há mais de uma cidade autorizada e manda para o estado da primeira cidade 
                // se não manda para primeira cidade
                try {
                    if(localStorage.getItem('currentMapCity') && JSON.parse(localStorage.getItem('currentMapCity'))){
                        try {
                            mapServices.current.changeMapCenter(JSON.parse(localStorage.getItem('currentMapCity')).coordinates);
                            setCurrentCitie(JSON.parse(localStorage.getItem('currentMapCity')).code)
                        } catch (error) {
                            console.log(error)
                        }
                    }else if(tempCities.authorized_cities.length > 2){
                        mapServices.current.changeMapCenter([tempCities.authorized_cities[1].state.coordinates.longitude, tempCities.authorized_cities[1].state.coordinates.latitude], 6.8);
                        setCurrentCitie('');
                    }else{
                        setCurrentCitie(tempCities.authorized_cities[1].code_ibge)
                        mapServices.current.changeMapCenter([tempCities.authorized_cities[1].coordinates.longitude, tempCities.authorized_cities[1].coordinates.latitude]);
                    }
                } catch (error) {
                    console.log(error)
                }
            } catch (error) {
                toast(error.message, {
                    position: "top-center",
                    type: 'error',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: false,
                    draggable: false,
                    progress: undefined,
                });
            }
        }

        mapServices.current = new MapService(mapContainerRef.current, navigate);
        StartAndGetCities();    
    }, []);

    const changeCity = (value) => {
        let newCity = cities.find(city => parseInt(city.code_ibge) === parseInt(value));
        if(newCity){
            mapServices.current.changeMapCenter([newCity.coordinates.longitude, newCity.coordinates.latitude]);
            localStorage.setItem('currentMapCity', JSON.stringify({
                coordinates: [newCity.coordinates.longitude, newCity.coordinates.latitude],
                code: newCity.code_ibge
            }));
            setCurrentCitie(newCity.code_ibge);
        }
    }

    return(

        <Container>
            {loading && <CustomLoading width={'200px'} height={'200px'}/>}
            <ContainerMap ref={mapContainerRef}>
            </ContainerMap>
            <SelectContainer>
                {cities.length > 0 && 
                    <SelectCitie disabled={loading} options={cities} value={currentCitie} onChange={(e) => changeCity(e.target.value)}/>
                }
            </SelectContainer>
            <LastUpdateText> 
                <LastUpdateIcon src={RelogioIcon}/>
                {TimeReset}
            </LastUpdateText>

            <CaptionContainer>
                <CaptionButton $showCaption={showCaption} onClick={() => store.dispatch(ChangeMapShowCaption(!showCaption))}>
                    {t("map.caption.label")}
                </CaptionButton>
                <CaptionContent id="caption" $showCaption={showCaption}>
                    <CaptionItem>
                        <strong>
                            {t("map.caption.equipment.label")}
                        </strong>
                        <CaptionGroup>
                            <CaptionImage src={Vazao} $width={'30px'} $height={'16px'}/>
                            {t("map.caption.equipment.flow")}
                        </CaptionGroup>
                        <CaptionGroup>
                            <CaptionImage src={Level} $width={'17px'} $height={'30px'}/>
                            {t("map.caption.equipment.level")}
                        </CaptionGroup>
                        <CaptionGroup>
                            <CaptionImage src={Pressure} $width={'31px'} $height={'23px'}/>
                            {t("map.caption.equipment.pressure")}
                        </CaptionGroup>
                        <CaptionGroup>
                            <CaptionImage src={Reservatorio} $width={'21px'} $height={'25px'}/>
                            {t("map.caption.equipment.reservoir")}
                        </CaptionGroup>
                        <CaptionGroup>
                            <CaptionImage src={GeneralIcon} $width={'30px'} $height={'30px'}/>
                            {t("map.caption.equipment.analyzer")}
                        </CaptionGroup>
                        <CaptionGroup>
                            <CaptionImage src={Poco} $width={'30px'} $height={'30px'}/>
                            {t("map.caption.equipment.well")}
                        </CaptionGroup>
                        <CaptionGroup>
                            <CaptionImage src={ETA} $width={'65px'} $height={'30px'}/>
                            {t("map.caption.equipment.eta")}
                        </CaptionGroup>
                    </CaptionItem>
                    <CaptionItem>
                        <strong>
                            {t("map.caption.alarms.label")}
                        </strong>
                        <CaptionGroup>
                            <CaptionImg src={Critico}/>
                            {t("map.caption.alarms.critical")}
                        </CaptionGroup>
                        <CaptionGroup>
                            <CaptionImg src={Advertencia}/>
                            {t("map.caption.alarms.warning")}
                        </CaptionGroup>
                        <CaptionGroup>
                            <CaptionImg src={PerdaComunicacao}/>
                            {t("map.caption.alarms.communication-loss")}
                        </CaptionGroup>
                        <CaptionGroup>
                            <CaptionImg src={Manutencao}/>
                            {t("map.caption.alarms.maintenance")}
                        </CaptionGroup>
                    </CaptionItem>
                </CaptionContent>
            </CaptionContainer>

            <ListEquipamentos>
                {EquipamentDataList.map((equipamento, index) => (
                    <EquipamentCard key={index} id={[equipamento.id, "unpin-button"]} dados={equipamento.data} type={equipamento.type} equipamento={equipamento.name} status={equipamento.status}/>
                ))}
            </ListEquipamentos>

            <ModalMap title={equipamento.nome} name={'mapmodal'} width={'50%'} height={'fit-content'} 
                $background={status === 4 ? '#E1800E' : status === 3 ? '#6600CC' : status === 2 ? '#FF3939': status === 1 ? '#EABC1A' : status === 0 ? '#33CC99' : '#F6F6F6'}>
                {isLoadingModal ?
                    <CustomLoading width={'150px'} height={'150px'}/>
                :
                    <>
                        {dados?.data?.map((dado, index) => (
                            <ModalLine key={index} dado={dado} showvalue2={dados?.data.some(obj => obj.hasOwnProperty('value2'))}/>
                        ))}
                        {dados?.time && <LastUpdateEquipament> <LastUpdateIcon src={RelogioIcon}/> {dados.time}</LastUpdateEquipament>}
                    </>  
                }
            </ModalMap>
        </Container> 
    );
}

export default AppMap;